import React from "react";
import { format, startOfDay } from "date-fns";
import { Icon } from "@iconify/react";
import { HStack, VStack } from "../utils";
import { ExitDateBasis, ForfeitureSettings } from "../../types/EsopPlan";
import {
  computeLastDateToExercise,
  formatExerciseWindow,
  formatExpiryMode,
  getExitRule,
  mapReasonToExitType,
} from "../../utils/forfeitureUtils";

interface ForfeitureSummaryCardProps {
  /** The plan's forfeiture settings */
  forfeitureSettings: ForfeitureSettings;
  /** Separation reason string matching SeparationReasons constant values */
  separationReason: string;
  /** The employee's last working day */
  lastDay?: Date;
  /** Called when admin wants to override the lastDateToExercise */
  onOverrideLastDate?: (date: Date) => void;
  /** Over-ridden date set by admin */
  overriddenLastDate?: Date;
  /** Plan name for labelling */
  planName?: string;
}

export function ForfeitureSummaryCard({
  forfeitureSettings,
  separationReason,
  lastDay,
  onOverrideLastDate,
  overriddenLastDate,
  planName,
}: ForfeitureSummaryCardProps) {
  const exitType = mapReasonToExitType(separationReason);
  if (!exitType) return null;

  const rule = getExitRule(forfeitureSettings, exitType);

  if (!rule) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
        <HStack className="items-center gap-2">
          <Icon icon="fe:warning" width={18} color="#B45309" />
          <span>
            No forfeiture rule configured for{" "}
            <b className="capitalize">
              {separationReason.toLowerCase().replace(/_/g, " ")}
            </b>{" "}
            in {planName ? `plan "${planName}"` : "this plan"}.
          </span>
        </HStack>
      </div>
    );
  }

  const { vestedOptions, unvestedOptions } = rule;
  const computedLastDate =
    lastDay && !vestedOptions.exerciseWindow.neverExpires
      ? computeLastDateToExercise(lastDay, forfeitureSettings, separationReason)
      : undefined;

  const effectiveLastDate = overriddenLastDate ?? computedLastDate;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-4 text-xs">
      {/* Header */}
      <HStack className="items-center gap-2 text-blue-700">
        <Icon icon="mdi:information-outline" width={18} />
        <span className="font-semibold text-sm">
          Forfeiture rules for{" "}
          <span className="capitalize">
            {separationReason.toLowerCase().replace(/_/g, " ")}
          </span>
          {planName && (
            <span className="font-normal text-blue-600"> — {planName}</span>
          )}
        </span>
      </HStack>

      {/* Vested Options summary */}
      <VStack className="gap-1.5">
        <p className="font-semibold text-gray-700">Vested Options</p>
        {vestedOptions.exerciseWindow.neverExpires ? (
          <p className="text-gray-600">
            Employee can exercise vested options at any time — the window{" "}
            <span className="font-medium">never expires</span>.
          </p>
        ) : (
          <VStack className="gap-1">
            <p className="text-gray-600">
              Employee has{" "}
              <span className="font-medium">
                {formatExerciseWindow(vestedOptions.exerciseWindow)}
              </span>{" "}
              to exercise after their last day.
              {effectiveLastDate &&
                startOfDay(effectiveLastDate) >= startOfDay(new Date()) && (
                  <>
                    {" "}
                    Next expiry date:{" "}
                    <span className="font-semibold text-blue-700">
                      {format(effectiveLastDate, "dd MMM yyyy")}
                    </span>
                    {overriddenLastDate && computedLastDate && (
                      <span className="ml-1 text-orange-600">
                        (overridden from{" "}
                        {format(computedLastDate, "dd MMM yyyy")})
                      </span>
                    )}
                  </>
                )}
            </p>
            <p className="text-gray-500">
              After that, options will{" "}
              <span className="font-medium">
                {vestedOptions.onExpiry.toLowerCase()}
              </span>
              .
            </p>
          </VStack>
        )}

        {/* Override date input */}
        {onOverrideLastDate && computedLastDate && (
          <HStack className="items-center gap-3 mt-2">
            <label className="text-gray-500 whitespace-nowrap">
              Override next expiry date:
            </label>
            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400"
              value={
                overriddenLastDate
                  ? format(overriddenLastDate, "yyyy-MM-dd")
                  : computedLastDate
                  ? format(computedLastDate, "yyyy-MM-dd")
                  : ""
              }
              onChange={(e) => {
                const d = new Date(e.target.value);
                if (!Number.isNaN(d.getTime())) onOverrideLastDate(d);
              }}
            />
          </HStack>
        )}
      </VStack>

      {/* Unvested Options summary */}
      <VStack className="gap-1.5 border-t border-blue-200 pt-3">
        <p className="font-semibold text-gray-700">Unvested Options</p>
        {unvestedOptions.forfeitImmediately ? (
          <p className="text-gray-600">
            All unvested options will be{" "}
            <span className="font-medium">forfeited immediately</span> upon
            exit.
          </p>
        ) : (
          <p className="text-gray-600">
            Unvested options continue to vest for a grace period of{" "}
            <span className="font-medium">
              {unvestedOptions.gracePeriodDays ?? 0} day
              {(unvestedOptions.gracePeriodDays ?? 0) !== 1 ? "s" : ""}
            </span>{" "}
            after the exit date, then are forfeited.
          </p>
        )}
      </VStack>

      {/* Exit date basis + Active employee expiry note */}
      <div className="border-t border-blue-200 pt-2 text-gray-400 space-y-1">
        {forfeitureSettings.exitDateBasis && (
          <p>
            Exercise window starts from:{" "}
            <span className="text-gray-500 font-medium">
              {forfeitureSettings.exitDateBasis ===
              ExitDateBasis.LAST_WORKING_DATE
                ? "Last working date"
                : "Resignation date"}
            </span>
          </p>
        )}
        <span>
          Active employee expiry:{" "}
          <span className="text-gray-500">
            {formatExpiryMode(forfeitureSettings.activeEmployee)}
          </span>
        </span>
      </div>
    </div>
  );
}

export default ForfeitureSummaryCard;
